import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import { AppBar, Box, Chip, Link, styled, Toolbar, Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";
import type React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "src/design-system/core/TranslationContext";

import Logo from "src/components/Logo";
import useAuth from "src/hooks/useAuth";
import { PATH_DASHBOARD, PATH_SUPERADMIN } from "src/routesConstants";
import { isAdminUser } from "src/utils/permission";

import clsx from "clsx";
import { Trans } from "react-i18next";
import { type EnvInfos, useQueryEnvInfos } from "src/api/developmentInfos/getEnvInfos";
import { SHARED_LAYOUT_NAMESPACE, TOP_BAR_TK } from "src/layouts/SharedLayout/config";
import { Help } from "src/layouts/SharedLayout/TopBar/Help";
import { DevelopmentEnum } from "src/types/enum/DevelopmentEnum";
import Account from "./Account";
import MenuBar from "./Menu/MenuBar";
import { NotificationsModalContainer } from "./notifications/NotificationsModalContainer";
import PosParameter from "./PosParameter";
import { SuperAdminSearchBar } from "./superAdminSearchBar/SuperAdminSearchBar";
import { useTopBarStyles } from "./TopBar.styles";
import { TopBarSelect } from "./TopBarSelect/TopBarSelect";

export interface TopBarProps {
    hasMinimalDisplay?: boolean;
    className?: string;
    isFixed?: boolean;
}

export const StyledDivider = styled(Divider)(() => ({
    height: 30,
    border: "1px solid rgba(0, 0, 0, 0.12)",
}));

const TopBar: React.FC<TopBarProps> = ({ hasMinimalDisplay, className, isFixed = true }) => {
    const { user } = useAuth();
    const classes = useTopBarStyles();
    const { t } = useTranslation(`${SHARED_LAYOUT_NAMESPACE}:${TOP_BAR_TK}`);
    const hostInfos: EnvInfos = Object.values(DevelopmentEnum).includes(
        import.meta.env.VITE_FLYNT_ENV as DevelopmentEnum
    )
        ? useQueryEnvInfos().data
        : { host: "", replicaHost: "", db: "" };

    return (
        <>
            <AppBar
                className={clsx(classes.root, className)}
                position={isFixed ? "fixed" : "relative"}
                style={{ background: "transparent", boxShadow: "none" }}
            >
                <Toolbar className={classes.toolbar}>
                    <Box ml={4} mt="11px" mb="8px">
                        <Link
                            component={RouterLink}
                            to={isAdminUser(user) ? PATH_SUPERADMIN : PATH_DASHBOARD}
                        >
                            <Logo />
                        </Link>
                    </Box>
                    {Object.values(DevelopmentEnum).includes(
                        import.meta.env.VITE_FLYNT_ENV as DevelopmentEnum
                    ) && (
                        <Box ml={4}>
                            <Tooltip
                                title={
                                    <Trans
                                        i18nKey={t("database-used")}
                                        values={{
                                            host: hostInfos?.host,
                                            replicaHost: hostInfos?.replicaHost,
                                            db: hostInfos?.db,
                                        }}
                                    ></Trans>
                                }
                            >
                                <Chip
                                    variant="outlined"
                                    icon={<CodeRoundedIcon />}
                                    label={import.meta.env.VITE_FLYNT_ENV}
                                    color="primary"
                                />
                            </Tooltip>
                        </Box>
                    )}
                    <Box flexGrow={1} ml={5}>
                        {!hasMinimalDisplay && <MenuBar />}
                    </Box>
                    {!hasMinimalDisplay && (
                        <>
                            {isAdminUser(user) ? (
                                <SuperAdminSearchBar />
                            ) : (
                                <Box display="flex" gap={1.5} alignItems="center">
                                    <Box ml={2} mr={1}>
                                        <TopBarSelect key={`user-${user.id}`} />
                                    </Box>
                                    <Box>
                                        <StyledDivider
                                            orientation="vertical"
                                            variant="middle"
                                            className={classes.verticalDivider}
                                        />
                                    </Box>

                                    <Box>
                                        <Help />
                                    </Box>
                                    <Box ml={1}>
                                        <NotificationsModalContainer />
                                    </Box>
                                    <Box ml={2}>
                                        <PosParameter />
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                    <Box ml={2}>
                        <Account />
                    </Box>
                </Toolbar>
                <Divider light />
            </AppBar>
        </>
    );
};

export default TopBar;
